import { useEffect, useRef } from "react"
import { useUserSchoolSubscriptionQuery } from "@pathwright/graphql-client/src/generated"
import { OrderSubscriptionFlow } from "../../order/OrderSubscriptionCard"
import { LICENSING_MODE_PRIVATE_COHORTS } from "../../store/resource/offering/license/constants"

/**
 * School / private-cohorts subscribe gate.
 *
 * - paymentType === "subscription" → gate until on an included school plan
 * - paymentType === "free" | "one_off" → skip gate (seat create)
 * - paymentType undefined (ResourceAbout) → gate only when cost_per_seat < 0
 */
const LicenseResourcePrivateCohortsGate = ({
  licenseOffering,
  children,
  refetchLicenseOffering,
  redirectTo,
  paymentType
}) => {
  const normalizedPaymentType = paymentType
    ? String(paymentType).toLowerCase()
    : undefined

  const shouldGateOnSubscription =
    licenseOffering.licensing_mode === LICENSING_MODE_PRIVATE_COHORTS &&
    (normalizedPaymentType === "subscription" ||
      (normalizedPaymentType == null && licenseOffering.cost_per_seat < 0))

  const userSchoolSubscriptionQuery = useUserSchoolSubscriptionQuery({
    skip: !shouldGateOnSubscription
  })

  const userSchoolSubscription =
    userSchoolSubscriptionQuery.data?.me?.school_subscription
  const includedPlanIds = licenseOffering.include_in_subscription_plans.map(
    (subscriptionPlan) => subscriptionPlan.id
  )
  const needsToSubscribe =
    shouldGateOnSubscription &&
    (!userSchoolSubscription ||
      !includedPlanIds.includes(
        userSchoolSubscription.school_subscription_plan.id
      ))

  const prevNeedsToSubscribe = useRef(needsToSubscribe)
  useEffect(() => {
    if (prevNeedsToSubscribe.current && !needsToSubscribe) {
      refetchLicenseOffering && refetchLicenseOffering()
      userSchoolSubscriptionQuery.refetch()
    }
    prevNeedsToSubscribe.current = needsToSubscribe
  }, [needsToSubscribe, refetchLicenseOffering, userSchoolSubscriptionQuery])

  if (userSchoolSubscriptionQuery.loading) return null

  if (needsToSubscribe) {
    return (
      <OrderSubscriptionFlow
        planIds={includedPlanIds}
        productIds={[]}
        redirectTo={redirectTo}
      />
    )
  }

  return children
}

LicenseResourcePrivateCohortsGate.displayName =
  "LicenseResourcePrivateCohortsGate"

export default LicenseResourcePrivateCohortsGate
